import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    IDLE, PENDING, LIST, ADD, EDIT, DANGER, SUCCESS,
} from "const"
import { RrsTemplate } from "server/models"
import { StateType, deepCopy, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_RRS_TEMPLATE,
    StoreType,
    AccountSettings,
    ProjectSettings,
    RrsTemplateList,
    RrsTemplateFilters,

    LoadAccountSettings,
    LoadProjectSettings,
    SaveAccountSettings,
    SaveProjectSettings,

    CopyDefaultRrsCategories,
    CopyDefaultRrsTemplates,
    LoadAllRrsTemplates,
    SortRrsTemplate,
    DeleteRrsTemplate,
    GetMaxRrsTemplates,

    setSystemAccountSettings,
    setProjectProjectSettings,
    setRrsTemplateList,
    setRrsTemplateIds,
    setRrsTemplateSort,
    setRrsTemplateFilters,
    addModal,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageForm,
    Feedback,
    Button,
    Loader,
    Table,
    TableSearchWrapper,
    SortRows,
    DeleteRow,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import RrsTemplateSearch from "client/pages/search/rrsTemplateSearch"
import Columns from "./columns"
import Default from "./default"


const TemplateList = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const accountSettings = useSelector((state: StoreType) => state.system.accountSettings)
    const projectSettings = useSelector((state: StoreType) => state.project.projectSettings)
    const { defaultTemplatesAssigned } = project.id ? projectSettings.rrs : accountSettings.rrs

    const baseRoute = useSelector((state: StoreType) => state.rrsTemplate.baseRoute)
    const templateList = useSelector((state: StoreType) => state.rrsTemplate.templateList)
    const templateIds = useSelector((state: StoreType) => state.rrsTemplate.templateIds)
    const sortColumn = useSelector((state: StoreType) => state.rrsTemplate.sortColumn)
    const sortOrder = useSelector((state: StoreType) => state.rrsTemplate.sortOrder)
    const filters = useSelector((state: StoreType) => state.rrsTemplate.filters)
    const reloadList = useSelector((state: StoreType) => state.rrsTemplate.reloadList)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [importState, setImportState] = useState<StateType>(IDLE)

    useEffect(() => {
        if (userData.accountId === 1) return

        if (!project.id && !accountSettings.rrs.defaultTemplatesAssigned) {
            setImportState(PENDING)
            LoadAccountSettings(userData.accountId)
            .then((settings) => {
                if (!settings.rrs.defaultTemplatesAssigned) {
                    // forced import of categories even if they were imported before - to avoid issues
                    CopyDefaultRrsCategories(userData.accountId, null, false)
                    .then(() => {
                        const newSettings: AccountSettings = deepCopy(settings)
                        newSettings.rrs.defaultCategoriesAssigned = true

                        CopyDefaultRrsTemplates(userData.accountId, null, false)
                        .then(() => {
                            newSettings.rrs.defaultTemplatesAssigned = true

                            SaveAccountSettings(userData.accountId, newSettings)
                            .then(() => {
                                dispatch(setSystemAccountSettings(newSettings))
                                setImportState(IDLE)
                            })
                        })
                    })
                } else {
                    setImportState(IDLE)
                }
            })
        }

        if (project.id && !projectSettings.rrs.defaultTemplatesAssigned) {
            setImportState(PENDING)
            LoadProjectSettings(project.id)
            .then((settings) => {
                if (!settings.rrs.defaultTemplatesAssigned) {
                    // copy only templates, all used categories will be copied automatically
                    CopyDefaultRrsTemplates(userData.accountId, project.id, accountSettings.rrs.defaultTemplatesAssigned)
                    .then(() => {
                        const newSettings: ProjectSettings = deepCopy(settings)
                        newSettings.rrs.defaultCategoriesAssigned = true
                        newSettings.rrs.defaultTemplatesAssigned = true

                        SaveProjectSettings(project.id, newSettings)
                        .then(() => {
                            dispatch(setProjectProjectSettings(newSettings))
                            setImportState(IDLE)
                        })
                    })
                } else {
                    setImportState(IDLE)
                }
            })
        }
    }, [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        LoadAllRrsTemplates(userData.accountId, project.id).then((res) => {
            dispatch(setRrsTemplateList(res))
            setLoadState(IDLE)
        })
    }, [reloadList, defaultTemplatesAssigned])

    useEffect(() => {
        const sortedIds = SortRrsTemplate(Object.values(templateList), sortColumn, sortOrder, filters)
        dispatch(setRrsTemplateIds(sortedIds))
    }, [templateList, sortColumn, sortOrder, filters])

    const onSort: SortRows = (column: keyof RrsTemplate, order) => {
        dispatch(setRrsTemplateSort(column, order))
    }

    const onFilter = (newFilters: RrsTemplateFilters) => {
        dispatch(setRrsTemplateFilters(newFilters))
    }

    const onDelete: DeleteRow = (template: RrsTemplate, callback) => {
        DeleteRrsTemplate(template.id, userData.accountId, project.id).then((removedRows) => {
            const success = removedRows === 1
            callback(success, () => {
                const templateListCopy: RrsTemplateList = templateList ? deepCopy(templateList) : {}
                delete templateListCopy[template.id]
                dispatch(setRrsTemplateList(templateListCopy))
                dispatch(setRrsTemplateIds(templateIds.filter((id) => id !== template.id)))
            })
        })
    }

    // max number of templates
    const templatesNumber = Object.keys(templateList).length
    const { maxTemplates, feedback } = GetMaxRrsTemplates(packageSettings, userData, templatesNumber, lang)

    const maxTemplatesReached = maxTemplates !== null && templatesNumber >= maxTemplates
    const maxTemplatesExceeded = maxTemplates !== null && templatesNumber > maxTemplates

    const columns = Columns()

    const listAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, LIST)
        : hasProjectAccess(PRM_RRS_TEMPLATE, LIST)

    const addAccess = !project.id
        ? hasAccess(PRM_RRS_TEMPLATE, ADD)
        : hasProjectAccess(PRM_RRS_TEMPLATE, ADD)

    if (!listAccess) return <AccessDenied />

    if (project.id) {
        return (
            <Container>
                <PageHeader>
                    <PageTitle>{lang.templates}</PageTitle>
                </PageHeader>
                <PageContent>
                    {
                        (loadState === PENDING || importState === PENDING)
                        && <Loader />
                    }
                    <Default />
                </PageContent>
            </Container>
        )
    }

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.templates}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    (loadState === PENDING || importState === PENDING)
                    && <Loader />
                }
                <TableSearchWrapper>
                    <RrsTemplateSearch
                        id="rrsTemplateList"
                        rightOffset={addAccess}
                        filters={filters}
                        onFilter={onFilter}
                    />
                    {
                        addAccess && (
                            <Button
                                variant="success"
                                disabled={maxTemplatesReached}
                                onClick={() => dispatch(addModal({ url: `${baseRoute}/${EDIT}/0` }))}
                            >
                                {lang.add}
                            </Button>
                        )
                    }
                </TableSearchWrapper>
                <Table
                    columns={columns}
                    dataList={templateList}
                    dataIds={templateIds}
                    sortColumn={sortColumn}
                    sortOrder={sortOrder}
                    rowSize="sm"
                    showPagination
                    onSort={onSort}
                    onDelete={onDelete}
                />
                {
                    userData.accountId !== 1 && (
                        <PageForm>
                            <Feedback variant={maxTemplatesExceeded ? DANGER : SUCCESS}>{feedback}</Feedback>
                        </PageForm>
                    )
                }
            </PageContent>
        </Container>
    )
}
export default TemplateList
